import { isSelectableMatchupSearchPlayer } from './clubPlayerSearchUtils';
import { resolveMatchupGameTypeIds } from './matchupFilters';
import { getPlayerFullName } from './playerUtils';

export const MATCHUP_HIGHLIGHT_MIN_GAMES_OPTIONS = [3, 5, 10, 20];
export const DEFAULT_MATCHUP_HIGHLIGHT_MIN_GAMES = 5;
export const MATCHUP_HIGHLIGHT_PREVIEW = 3;
export const MATCHUP_HIGHLIGHT_LIMIT = 10;

function sortHighlightRows(a, b) {
  if (b.winRate !== a.winRate) return b.winRate - a.winRate;
  if (b.games !== a.games) return b.games - a.games;
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

function toRow(stats) {
  const games = stats.games;
  return {
    id: stats.id,
    first_name: stats.first_name,
    last_name: stats.last_name,
    gender: stats.gender,
    name: stats.name,
    wins: stats.wins,
    losses: stats.losses,
    games,
    winRate: games > 0 ? (stats.wins / games) * 100 : 0,
  };
}

function ensurePlayerStats(map, player) {
  if (!map.has(player.id)) {
    map.set(player.id, {
      id: player.id,
      first_name: player.first_name,
      last_name: player.last_name,
      gender: player.gender,
      name: getPlayerFullName(player),
      wins: 0,
      losses: 0,
      games: 0,
    });
  }
  return map.get(player.id);
}

/**
 * Rank teammates and opponents by the current player's win rate.
 *
 * @param {Array<{
 *   gameTypeId?: number,
 *   pairType?: string,
 *   pair_type?: string,
 *   winningTeam?: number|null,
 *   players: Array<{
 *     id: string,
 *     teamNumber: number,
 *     first_name?: string,
 *     last_name?: string,
 *     gender?: string,
 *     is_guest?: boolean,
 *     is_active?: boolean,
 *     deletion_requested_at?: string|null,
 *     merged_at?: string|null,
 *     merged_into_user_id?: string|null,
 *   }>
 * }>} games
 * @param {{
 *   currentPlayerId: string,
 *   minGames?: number,
 *   balancedOnly?: boolean,
 *   gameType?: string,
 *   gameTypeIds?: number[],
 *   hasActiveClubMembershipByPlayerId?: Record<string, boolean>,
 *   limit?: number,
 * }} [options]
 */
export function buildMatchupHighlights(games, {
  currentPlayerId,
  minGames = DEFAULT_MATCHUP_HIGHLIGHT_MIN_GAMES,
  balancedOnly = true,
  gameType = 'all',
  gameTypeIds,
  hasActiveClubMembershipByPlayerId = {},
  limit = MATCHUP_HIGHLIGHT_LIMIT,
} = {}) {
  const withMap = new Map();
  const againstMap = new Map();
  const allowedTypeIds = new Set(
    resolveMatchupGameTypeIds({ gameTypeIds, gameType, balancedOnly })
  );

  (games || []).forEach((game) => {
    if (!allowedTypeIds.has(Number(game.gameTypeId ?? game.game_type_id))) return;

    const players = game.players || [];
    const current = players.find((p) => p.id === currentPlayerId);
    if (!current) return;

    const won = game.winningTeam === current.teamNumber;

    players.forEach((player) => {
      if (!player?.id || player.id === currentPlayerId) return;
      if (
        !isSelectableMatchupSearchPlayer(player, {
          hasActiveClubMembership: hasActiveClubMembershipByPlayerId[player.id] === true,
        })
      ) {
        return;
      }

      const map = player.teamNumber === current.teamNumber ? withMap : againstMap;
      const row = ensurePlayerStats(map, player);
      row.games += 1;
      if (won) row.wins += 1;
      else if (game.winningTeam != null) row.losses += 1;
    });
  });

  const filterAndSort = (map) =>
    [...map.values()]
      .map(toRow)
      .filter((row) => row.games >= minGames)
      .sort(sortHighlightRows)
      .slice(0, limit);

  return {
    bestWith: filterAndSort(withMap),
    bestAgainst: filterAndSort(againstMap),
  };
}
